/**
 * Scroll reveal: content in the main region floats up as it enters the viewport.
 *
 * Progressive enhancement only. Nothing is hidden until this module runs, anything already on
 * screen at load stays untouched, and the effect is skipped for reduced-motion users, in the
 * theme editor, and in browsers without IntersectionObserver. Loaded from snippets/scripts.liquid
 * when the "reveal_on_scroll" theme setting is on; styles live in base.css ([data-reveal]).
 */

const LEAF_SELECTOR = [
  '.text-block',
  '.image-block',
  '.button',
  '.button-secondary',
  'product-price',
  '.buy-buttons-block',
  '.accordion',
  'media-gallery',
  '.product-hero__actions',
  '.product-hero__status',
  '.product-hero__note',
  '.product-hero__media',
  '.ingredient-tier__header',
  '.ingredient-card',
].join(',');

// The intro hero and accordion ingredient tiers have their own entrances; sticky bars and dialogs
// must never be hidden.
const SKIP_SELECTOR = '[data-no-reveal], .product-hero--intro, .ingredient-tier__panel, sticky-add-to-cart, dialog, [popover]';

const STAGGER_MS = 140;
const MAX_STAGGER_STEPS = 5;
// Matches the transition in base.css plus the largest stagger, so the attribute is always cleared.
const CLEANUP_MS = 1000 + STAGGER_MS * MAX_STAGGER_STEPS + 100;

/**
 * Cards and columns move as one: use the outermost group that sits directly in a row layout.
 * @param {Element} leaf
 * @param {Element} section
 * @returns {Element}
 */
function revealUnitFor(leaf, section) {
  let unit = leaf;
  for (let node = leaf.parentElement; node && node !== section; node = node.parentElement) {
    if (node.classList.contains('group-block') && node.parentElement?.classList.contains('layout-panel-flex--row')) {
      unit = node;
    }
  }
  return unit;
}

/**
 * @param {Element} element
 */
function isInViewport(element) {
  const rect = element.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < window.innerHeight;
}

/**
 * @param {HTMLElement} element
 * @param {number} delay
 */
function reveal(element, delay = 0) {
  if (element.dataset.reveal !== 'hidden') return;
  element.style.setProperty('--reveal-delay', `${delay}ms`);
  element.dataset.reveal = 'shown';
  // Drop the attribute afterwards so the element's own transitions (hover, focus) apply again.
  window.setTimeout(() => {
    delete element.dataset.reveal;
    element.style.removeProperty('--reveal-delay');
  }, CLEANUP_MS + delay);
}

function init() {
  if (window.Shopify?.designMode) return;
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const entering = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);

      entering.forEach((entry, index) => {
        observer.unobserve(entry.target);
        if (entry.target instanceof HTMLElement) {
          reveal(entry.target, Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  for (const section of document.querySelectorAll('main .shopify-section')) {
    const units = new Set();

    for (const leaf of section.querySelectorAll(LEAF_SELECTOR)) {
      if (leaf.closest(SKIP_SELECTOR)) continue;
      // Skip leaves nested inside another leaf (e.g. a button inside the buy buttons block).
      const outerLeaf = leaf.parentElement?.closest(LEAF_SELECTOR);
      if (outerLeaf && section.contains(outerLeaf)) continue;
      units.add(revealUnitFor(leaf, section));
    }

    for (const unit of units) {
      if (!(unit instanceof HTMLElement) || isInViewport(unit)) continue;
      unit.dataset.reveal = 'hidden';
      observer.observe(unit);
    }
  }

  // Keyboard users tabbing ahead of the scroll position see focused content immediately.
  document.addEventListener('focusin', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-reveal="hidden"]') : null;
    if (target instanceof HTMLElement) {
      observer.unobserve(target);
      reveal(target);
    }
  });
}

init();
